import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  connectorApi,
  mappingApi,
  sessionApi,
  type ConnectorItem,
  type MappingItem,
  type SessionItem,
} from '../api/integrationApi'

type TabKey = 'connectors' | 'mappings' | 'sessions'

export default function IntegrationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<TabKey>('connectors')
  const [search, setSearch] = useState('')

  // Connector state
  const [connectors, setConnectors] = useState<ConnectorItem[]>([])
  const [connectorTotal, setConnectorTotal] = useState(0)
  const [connectorPage, setConnectorPage] = useState(1)

  // Mapping state
  const [mappings, setMappings] = useState<MappingItem[]>([])
  const [mappingTotal, setMappingTotal] = useState(0)
  const [mappingPage, setMappingPage] = useState(1)

  // Session state
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [sessionTotal, setSessionTotal] = useState(0)
  const [sessionPage, setSessionPage] = useState(1)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pageSize = 20

  // ─── Fetch Data ───

  const fetchConnectors = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await connectorApi.list({ search, page: connectorPage, pageSize })
      setConnectors(res.items)
      setConnectorTotal(res.totalCount)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [search, connectorPage, t])

  const fetchMappings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await mappingApi.list({ search, page: mappingPage, pageSize })
      setMappings(res.items)
      setMappingTotal(res.totalCount)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [search, mappingPage, t])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await sessionApi.list({ page: sessionPage, pageSize })
      setSessions(res.items)
      setSessionTotal(res.totalCount)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('integrations.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [sessionPage, t])

  useEffect(() => {
    if (activeTab === 'connectors') fetchConnectors()
    else if (activeTab === 'mappings') fetchMappings()
    else if (activeTab === 'sessions') fetchSessions()
  }, [activeTab, fetchConnectors, fetchMappings, fetchSessions])

  // ─── Actions ───

  const handleDeleteConnector = async (id: number) => {
    if (!window.confirm(t('integrations.deleteConfirm.connector'))) return
    try {
      await connectorApi.delete(id)
      fetchConnectors()
    } catch {
      setError(t('integrations.errors.deleteFailed'))
    }
  }

  const handleDeleteMapping = async (id: number) => {
    if (!window.confirm(t('integrations.deleteConfirm.mapping'))) return
    try {
      await mappingApi.delete(id)
      fetchMappings()
    } catch {
      setError(t('integrations.errors.deleteFailed'))
    }
  }

  const handleTestConnector = async (id: number) => {
    try {
      await connectorApi.test(id)
      fetchConnectors()
    } catch {
      setError(t('integrations.errors.testFailed'))
    }
  }

  // ─── Helpers ───

  const connectorTypeIcon = (type: string) => {
    switch (type) {
      case 'api': return '☁️'
      case 'database': return '🗄️'
      case 'file': return '📄'
      case 'webhook': return '⚡'
      default: return '🔗'
    }
  }

  const statusBadge = (status: string | null) => {
    if (!status) return null
    const colors: Record<string, string> = {
      success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {t(`integrations.status.${status}`)}
      </span>
    )
  }

  const totalPages = Math.ceil(
    (activeTab === 'connectors' ? connectorTotal : activeTab === 'mappings' ? mappingTotal : sessionTotal) / pageSize
  )
  const currentPage = activeTab === 'connectors' ? connectorPage : activeTab === 'mappings' ? mappingPage : sessionPage
  const setCurrentPage = activeTab === 'connectors' ? setConnectorPage : activeTab === 'mappings' ? setMappingPage : setSessionPage

  // ─── Render ───

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('integrations.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('integrations.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/system/integrations/builder')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <span>+</span>
            {t('integrations.actions.newIntegration')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        {(['connectors', 'mappings', 'sessions'] as TabKey[]).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSearch('') }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {t(`integrations.tabs.${tab}`)}
            <span className="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
              {tab === 'connectors' ? connectorTotal : tab === 'mappings' ? mappingTotal : sessionTotal}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      {activeTab !== 'sessions' && (
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('integrations.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {/* Connectors Tab */}
      {!loading && activeTab === 'connectors' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.type')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.name')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.status')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.lastTested')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.active')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {connectors.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('integrations.emptyState.connectors')}</td></tr>
              ) : connectors.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3 text-lg">{connectorTypeIcon(c.type)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{c.name}</div>
                    {c.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.description}</div>}
                  </td>
                  <td className="px-4 py-3">{statusBadge(c.lastTestedStatus)}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {c.lastTestedAt ? new Date(c.lastTestedAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`w-2 h-2 rounded-full inline-block ${c.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleTestConnector(c.id)} className="px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors" title={t('integrations.actions.test')}>
                        {t('integrations.actions.test')}
                      </button>
                      <button onClick={() => navigate(`/system/integrations/connectors/${c.id}/edit`)} className="px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        {t('integrations.actions.edit')}
                      </button>
                      <button onClick={() => handleDeleteConnector(c.id)} className="px-2 py-1 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                        {t('integrations.actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mappings Tab */}
      {!loading && activeTab === 'mappings' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.name')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.sourceSystem')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.targetEntity')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.connector')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.active')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {mappings.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('integrations.emptyState.mappings')}</td></tr>
              ) : mappings.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{m.name}</div>
                    {m.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{m.sourceSystem}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">{m.targetEntity}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{m.connectorName || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`w-2 h-2 rounded-full inline-block ${m.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => navigate(`/system/integrations/mappings/${m.id}/edit`)} className="px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        {t('integrations.actions.edit')}
                      </button>
                      <button onClick={() => handleDeleteMapping(m.id)} className="px-2 py-1 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                        {t('integrations.actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sessions Tab */}
      {!loading && activeTab === 'sessions' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.mapping')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.targetEntity')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.status')}</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.rows')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.duration')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.executedAt')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">{t('integrations.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sessions.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">{t('integrations.emptyState.sessions')}</td></tr>
              ) : sessions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{s.mappingName}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">{s.targetEntity}</span>
                  </td>
                  <td className="px-4 py-3">{statusBadge(s.status)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-emerald-600 dark:text-emerald-400">{s.successCount}</span>
                    {s.errorCount > 0 && <span className="text-red-600 dark:text-red-400 ml-1">/ {s.errorCount}</span>}
                    <span className="text-gray-400 ml-1">/ {s.totalRows}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {s.durationMs ? `${(s.durationMs / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {s.startedAt ? new Date(s.startedAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/system/integrations/sessions/${s.id}`)}
                      className="px-2 py-1 text-xs bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      {t('integrations.actions.viewDetails')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            {t('integrations.pagination.showing', {
              from: (currentPage - 1) * pageSize + 1,
              to: Math.min(currentPage * pageSize, activeTab === 'connectors' ? connectorTotal : activeTab === 'mappings' ? mappingTotal : sessionTotal),
              total: activeTab === 'connectors' ? connectorTotal : activeTab === 'mappings' ? mappingTotal : sessionTotal,
            })}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
            >
              {t('integrations.pagination.prev')}
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
            >
              {t('integrations.pagination.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
